import { useState } from 'react';
import { PlayIcon, StopIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import type { WorkbookEditor } from './useWorkbookEditor';
import { hasActiveRun } from './useWorkbookQueries';
import { WorkbookSampleRunMenu } from './WorkbookSampleRunMenu';

/**
 * The toolbar's Run split button: Run the whole Workbook, with the other ways
 * to run behind its caret. While a Run starts, is queued or runs, it looks the
 * same but does nothing: the footer shows the Run's progress, and a second
 * Run never starts. Other busy work (a save, a download) disables it. A Run the
 * editor started can be cancelled beside it; the caret stays open for
 * Sampling, with Run on sample disabled.
 */
export function WorkbookRunButton({
  editor,
  busy,
  blockedBy,
  canUpdate,
  canExecute,
  act,
}: {
  editor: WorkbookEditor;
  busy: boolean;
  /** The id of what blocks the Run, if anything does. */
  blockedBy: string | undefined;
  canUpdate: boolean;
  canExecute: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const gt = useGT();
  // Only this button's own start counts: `busy` covers saves and downloads too.
  const [starting, setStarting] = useState(false);
  const running = starting || hasActiveRun(editor.runs.data);
  function run() {
    if (running) return;
    setStarting(true);
    // A promise chain, not try/finally: the React Compiler can't lower the latter.
    void act(editor.run).finally(() => setStarting(false));
  }
  // Only Runs the editor started have a workflow of their own to cancel.
  const cancellable = editor.runs.data?.find(
    (candidate) =>
      (candidate.status === 'queued' || candidate.status === 'running') &&
      Boolean(candidate.workflowRunId),
  );
  const empty = !editor.definition.sheets.length;
  return (
    <div className="flex items-center">
      <Button
        size="sm"
        className="rounded-r-none"
        // Its own start makes the workspace busy too; that only holds it, as running does.
        disabled={
          (busy && !starting) || !canExecute || empty || blockedBy !== undefined
        }
        aria-label={gt('Run workbook', { context: 'workbook run' })}
        aria-disabled={running || undefined}
        aria-describedby={blockedBy}
        title={
          running
            ? gt('A run is in progress. Its status shows at the bottom.', {
                context: 'workbook run button while a run is in progress',
              })
            : undefined
        }
        onClick={run}
      >
        <PlayIcon />
        <T context="workbook run, short">Run</T>
      </Button>
      <WorkbookSampleRunMenu
        editor={editor}
        disabled={busy || empty}
        running={running}
        canUpdate={canUpdate}
        canExecute={canExecute}
        act={act}
      />
      {cancellable && canExecute ? (
        <Button
          size="sm"
          variant="outline"
          className="ml-2"
          // `act` shares one busy flag: never overlap a save, a download or this cancel's own request.
          disabled={busy}
          aria-label={gt('Cancel run', { context: 'workbook run' })}
          onClick={() => void act(() => editor.cancelRun(cancellable.id))}
        >
          <StopIcon />
          <T context="workbook run, short">Cancel</T>
        </Button>
      ) : null}
    </div>
  );
}
